import { useState, useEffect, useCallback } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import Card from '../components/common/Card';
import Badge from '../components/common/Badge';
import LoadingSpinner from '../components/common/LoadingSpinner';
import SystemStatus from '../components/admin/SystemStatus';
import UsersTable from '../components/admin/UsersTable';
import { getPredictionPerformance, getTopPicksStats, getFeaturedPick, getUserStats } from '../services/adminService';

// Tab definitions
const TABS = [
  { id: 'status', label: 'System Status', icon: '!' },
  { id: 'predictions', label: 'Predictions', icon: '%' },
  { id: 'users', label: 'Users', icon: '@' }
];

// Sport display config
const SPORT_CONFIG = {
  nba: { name: 'NBA', color: 'text-orange-400' },
  nhl: { name: 'NHL', color: 'text-blue-400' },
  nfl: { name: 'NFL', color: 'text-green-400' },
  mlb: { name: 'MLB', color: 'text-red-400' },
  ncaa_f: { name: 'NCAAF', color: 'text-purple-400' }
};

export default function Analytics() {
  const [activeTab, setActiveTab] = useState('status');
  const [loading, setLoading] = useState(false);
  const [selectedSport, setSelectedSport] = useState('all');
  const [dateRange, setDateRange] = useState('30');

  // Data states
  const [predictionData, setPredictionData] = useState(null);
  const [topPicksData, setTopPicksData] = useState(null);
  const [featuredPick, setFeaturedPick] = useState(null);
  const [userData, setUserData] = useState(null);
  const [error, setError] = useState(null);

  const sports = ['all', 'nba', 'nhl', 'nfl', 'mlb', 'ncaa_f'];

  // Fetch prediction data
  const fetchPredictionData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const today = new Date();
      let startDate, endDate;

      if (dateRange === '0') {
        // Today only
        startDate = today.toISOString().split('T')[0];
        endDate = startDate;
      } else if (dateRange === '1') {
        // Yesterday only
        const yesterday = new Date(today);
        yesterday.setDate(yesterday.getDate() - 1);
        startDate = yesterday.toISOString().split('T')[0];
        endDate = startDate;
      } else {
        // Last N days
        endDate = today.toISOString().split('T')[0];
        startDate = new Date(Date.now() - parseInt(dateRange) * 24 * 60 * 60 * 1000)
          .toISOString()
          .split('T')[0];
      }

      const [predPerf, topPicks, featured] = await Promise.all([
        getPredictionPerformance(
          selectedSport === 'all' ? null : selectedSport,
          startDate,
          endDate
        ),
        getTopPicksStats(
          selectedSport === 'all' ? null : selectedSport,
          startDate,
          endDate
        ),
        getFeaturedPick()
      ]);

      setPredictionData(predPerf);
      setTopPicksData(topPicks);
      setFeaturedPick(featured);
    } catch (err) {
      console.error('Error fetching prediction data:', err);
      setError(err.message || 'Failed to load prediction data');
    } finally {
      setLoading(false);
    }
  }, [selectedSport, dateRange]);

  // Fetch user data
  const fetchUserData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await getUserStats();
      setUserData(data);
    } catch (err) {
      console.error('Error fetching user data:', err);
      setError(err.message || 'Failed to load user data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (activeTab === 'predictions') {
      fetchPredictionData();
    } else if (activeTab === 'users') {
      fetchUserData();
    }
  }, [activeTab, fetchPredictionData, fetchUserData]);

  // Prepare chart data from API response
  const getChartData = () => {
    if (!predictionData?.by_sport) return [];
    return Object.entries(predictionData.by_sport)
      .filter(([sport]) => selectedSport === 'all' || sport === selectedSport)
      .map(([sport, data]) => ({
        sport: SPORT_CONFIG[sport]?.name || sport.toUpperCase(),
        accuracy: data.accuracy || 0,
        total: data.total || 0
      }))
      .filter(item => item.total > 0);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="glass-card p-6">
        <h1 className="text-3xl font-bold text-gradient">Admin Dashboard</h1>
        <p className="mt-2 text-text-secondary">
          System status, model performance, and user analytics
        </p>

        {/* Tabs */}
        <div className="flex gap-2 mt-4 border-t border-border pt-4">
          {TABS.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`px-4 py-2 rounded-lg font-medium transition-all ${
                activeTab === tab.id
                  ? 'bg-accent-blue text-white'
                  : 'bg-bg-tertiary text-text-secondary hover:bg-bg-secondary hover:text-text-primary'
              }`}
            >
              <span className="mr-2">{tab.icon}</span>
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {/* System Status Tab */}
      {activeTab === 'status' && <SystemStatus />}

      {/* Predictions Tab */}
      {activeTab === 'predictions' && (
        <>
          {/* Filters */}
          <div className="glass-card p-4">
            <div className="flex flex-col sm:flex-row gap-4">
              {/* Sport Filter */}
              <div className="flex-1">
                <label className="block text-xs font-medium text-text-secondary mb-2">
                  Sport
                </label>
                <select
                  value={selectedSport}
                  onChange={(e) => setSelectedSport(e.target.value)}
                  className="w-full input-glow"
                >
                  {sports.map((sport) => (
                    <option key={sport} value={sport}>
                      {sport === 'all' ? 'All Sports' : SPORT_CONFIG[sport]?.name || sport.toUpperCase()}
                    </option>
                  ))}
                </select>
              </div>

              {/* Date Range */}
              <div className="flex-1">
                <label className="block text-xs font-medium text-text-secondary mb-2">
                  Date Range
                </label>
                <select
                  value={dateRange}
                  onChange={(e) => setDateRange(e.target.value)}
                  className="w-full input-glow"
                >
                  <option value="0">Today</option>
                  <option value="1">Yesterday</option>
                  <option value="7">Last 7 Days</option>
                  <option value="30">Last 30 Days</option>
                  <option value="90">Last 90 Days</option>
                  <option value="365">Last Year</option>
                </select>
              </div>

              <div className="flex items-end">
                <button
                  onClick={fetchPredictionData}
                  className="px-4 py-2 bg-accent-blue/20 text-accent-blue rounded-lg hover:bg-accent-blue/30 transition-colors"
                >
                  Refresh
                </button>
              </div>
            </div>
          </div>

          {error && (
            <Card className="p-4 bg-red-500/10 border border-red-500/30">
              <div className="text-red-400">{error}</div>
            </Card>
          )}

          {loading ? (
            <div className="flex justify-center py-12">
              <LoadingSpinner size="lg" text="Loading prediction data..." />
            </div>
          ) : predictionData && (
            <>
              {/* Prediction Performance Summary */}
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <Card className="p-6">
                  <div className="text-sm text-text-secondary mb-1">Total Predictions</div>
                  <div className="text-3xl font-bold text-text-primary font-mono">
                    {predictionData.totals?.total_predictions?.toLocaleString() || 0}
                  </div>
                </Card>
                <Card className="p-6">
                  <div className="text-sm text-text-secondary mb-1">Correct</div>
                  <div className="text-3xl font-bold text-success font-mono">
                    {predictionData.totals?.correct_predictions?.toLocaleString() || 0}
                  </div>
                </Card>
                <Card className="p-6">
                  <div className="text-sm text-text-secondary mb-1">Accuracy</div>
                  <div className="text-3xl font-bold text-accent-blue font-mono">
                    {predictionData.totals?.accuracy || 0}%
                  </div>
                </Card>
                <Card className="p-6">
                  <div className="text-sm text-text-secondary mb-1">Date Range</div>
                  <div className="text-lg font-mono text-text-primary">
                    {predictionData.date_range?.start} to {predictionData.date_range?.end}
                  </div>
                </Card>
              </div>

              {/* Performance by Sport */}
              <Card className="p-6">
                <h2 className="text-xl font-bold text-text-primary mb-4">Performance by Sport</h2>
                <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-6">
                  {Object.entries(predictionData.by_sport || {}).map(([sport, data]) => (
                    <div key={sport} className="bg-bg-tertiary rounded-lg p-4 text-center">
                      <div className={`text-lg font-semibold ${SPORT_CONFIG[sport]?.color || 'text-text-primary'}`}>
                        {SPORT_CONFIG[sport]?.name || sport.toUpperCase()}
                      </div>
                      <div className="text-2xl font-bold text-text-primary font-mono mt-2">
                        {data.accuracy}%
                      </div>
                      <div className="text-xs text-text-secondary mt-1">
                        {data.correct}/{data.total} correct
                      </div>
                    </div>
                  ))}
                </div>

                {/* Bar Chart */}
                {getChartData().length > 0 && (
                  <ResponsiveContainer width="100%" height={250}>
                    <BarChart data={getChartData()}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#2D3452" />
                      <XAxis dataKey="sport" stroke="#8B92B0" />
                      <YAxis stroke="#8B92B0" domain={[0, 100]} />
                      <Tooltip
                        contentStyle={{
                          backgroundColor: '#1A1F3A',
                          border: '1px solid #2D3452',
                          borderRadius: '8px',
                          color: '#FFFFFF'
                        }}
                        formatter={(value) => [`${value}%`, 'Accuracy']}
                      />
                      <Bar dataKey="accuracy" fill="#00D9FF" radius={[8, 8, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                )}
              </Card>

              {/* Featured Pick */}
              {featuredPick && featuredPick.pick && (
                <Card className="p-6 border border-accent-blue/30 bg-accent-blue/5">
                  <div className="flex items-center gap-2 mb-4">
                    <span className="text-xl">*</span>
                    <h2 className="text-xl font-bold text-text-primary">Featured Pick of the Day</h2>
                  </div>
                  <div className="flex flex-col md:flex-row gap-4 items-start md:items-center">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <Badge variant="info" size="sm">{featuredPick.pick.sport?.toUpperCase()}</Badge>
                        <Badge variant="primary" size="sm">{featuredPick.pick.pick_type?.replace(/_/g, ' ')}</Badge>
                        {featuredPick.pick.is_correct !== null && (
                          <Badge
                            variant={featuredPick.pick.is_correct ? 'success' : 'danger'}
                            size="sm"
                          >
                            {featuredPick.pick.is_correct ? 'WIN' : 'LOSS'}
                          </Badge>
                        )}
                      </div>
                      <div className="text-lg font-semibold text-text-primary">
                        {featuredPick.pick.player_name && `${featuredPick.pick.player_name} - `}
                        {featuredPick.pick.prop_type && `${featuredPick.pick.prop_type} `}
                        {featuredPick.pick.direction && featuredPick.pick.line && (
                          <span className={featuredPick.pick.direction === 'over' ? 'text-success' : 'text-red-400'}>
                            {featuredPick.pick.direction.toUpperCase()} {featuredPick.pick.line}
                          </span>
                        )}
                      </div>
                      <div className="text-sm text-text-secondary mt-1">
                        {featuredPick.pick.matchup || `${featuredPick.pick.away_team} @ ${featuredPick.pick.home_team}`}
                      </div>
                    </div>
                    <div className="flex gap-4">
                      <div className="text-center">
                        <div className="text-2xl font-bold text-accent-blue font-mono">
                          {featuredPick.pick.composite_score || featuredPick.pick.confidence || '-'}
                        </div>
                        <div className="text-xs text-text-secondary">Confidence</div>
                      </div>
                      {featuredPick.pick.actual_value !== null && featuredPick.pick.actual_value !== undefined && (
                        <div className="text-center">
                          <div className="text-2xl font-bold text-warning font-mono">
                            {featuredPick.pick.actual_value}
                          </div>
                          <div className="text-xs text-text-secondary">Actual</div>
                        </div>
                      )}
                    </div>
                  </div>
                </Card>
              )}

              {/* Top Picks Performance */}
              {topPicksData && (
                <Card className="p-6">
                  <h2 className="text-xl font-bold text-text-primary mb-4">
                    Top Picks Performance
                    {selectedSport !== 'all' && (
                      <span className={`ml-2 text-sm ${SPORT_CONFIG[selectedSport]?.color || 'text-text-secondary'}`}>
                        ({SPORT_CONFIG[selectedSport]?.name || selectedSport.toUpperCase()})
                      </span>
                    )}
                  </h2>

                  {/* Summary Stats */}
                  <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
                    <div className="bg-bg-tertiary rounded-lg p-4 text-center">
                      <div className="text-2xl font-bold text-text-primary font-mono">
                        {topPicksData.totals?.total_picks || 0}
                      </div>
                      <div className="text-xs text-text-secondary">Total Picks</div>
                    </div>
                    <div className="bg-bg-tertiary rounded-lg p-4 text-center">
                      <div className="text-2xl font-bold text-success font-mono">
                        {topPicksData.totals?.winning_picks || 0}
                      </div>
                      <div className="text-xs text-text-secondary">Wins</div>
                    </div>
                    <div className="bg-bg-tertiary rounded-lg p-4 text-center">
                      <div className="text-2xl font-bold text-red-400 font-mono">
                        {topPicksData.totals?.losing_picks || 0}
                      </div>
                      <div className="text-xs text-text-secondary">Losses</div>
                    </div>
                    <div className="bg-bg-tertiary rounded-lg p-4 text-center">
                      <div className="text-2xl font-bold text-accent-blue font-mono">
                        {topPicksData.totals?.win_rate || 0}%
                      </div>
                      <div className="text-xs text-text-secondary">Win Rate</div>
                    </div>
                    <div className="bg-bg-tertiary rounded-lg p-4 text-center">
                      <div className="text-2xl font-bold text-warning font-mono">
                        {topPicksData.totals?.avg_confidence || 0}
                      </div>
                      <div className="text-xs text-text-secondary">Avg Confidence</div>
                    </div>
                  </div>

                  {/* Featured Pick Performance */}
                  {topPicksData.featured && topPicksData.featured.total_picks > 0 && (
                    <div className="mb-6">
                      <h3 className="text-sm font-medium text-text-secondary uppercase tracking-wider mb-3">Featured Pick Performance</h3>
                      <div className="bg-accent-blue/10 border border-accent-blue/30 rounded-lg p-4">
                        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                          <div className="text-center">
                            <div className="text-2xl font-bold text-text-primary font-mono">
                              {topPicksData.featured.total_picks}
                            </div>
                            <div className="text-xs text-text-secondary">Total</div>
                          </div>
                          <div className="text-center">
                            <div className="text-2xl font-bold text-success font-mono">
                              {topPicksData.featured.winning_picks}
                            </div>
                            <div className="text-xs text-text-secondary">Wins</div>
                          </div>
                          <div className="text-center">
                            <div className="text-2xl font-bold text-red-400 font-mono">
                              {topPicksData.featured.losing_picks}
                            </div>
                            <div className="text-xs text-text-secondary">Losses</div>
                          </div>
                          <div className="text-center">
                            <div className="text-2xl font-bold text-accent-blue font-mono">
                              {topPicksData.featured.win_rate}%
                            </div>
                            <div className="text-xs text-text-secondary">Win Rate</div>
                          </div>
                          <div className="text-center">
                            <div className="text-2xl font-bold text-warning font-mono">
                              {topPicksData.featured.avg_confidence}
                            </div>
                            <div className="text-xs text-text-secondary">Avg Confidence</div>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* By Pick Type */}
                  {Object.keys(topPicksData.by_pick_type || {}).length > 0 && (
                    <div className="mb-6">
                      <h3 className="text-sm font-medium text-text-secondary uppercase tracking-wider mb-3">By Pick Type</h3>
                      <div className="flex flex-wrap gap-2">
                        {Object.entries(topPicksData.by_pick_type).map(([type, data]) => (
                          <div key={type} className="bg-bg-tertiary rounded-lg px-4 py-2">
                            <span className="text-text-primary font-medium">{type}</span>
                            <span className="ml-2 text-success font-mono">{data.win_rate}%</span>
                            <span className="ml-1 text-text-secondary text-xs">({data.wins}/{data.resolved})</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* By Confidence Tier */}
                  {Object.keys(topPicksData.by_confidence_tier || {}).length > 0 && (
                    <div>
                      <h3 className="text-sm font-medium text-text-secondary uppercase tracking-wider mb-3">By Confidence Tier</h3>
                      <div className="grid grid-cols-3 gap-4">
                        {['high', 'medium', 'low'].map((tier) => {
                          const data = topPicksData.by_confidence_tier[tier] || { total: 0, wins: 0, resolved: 0, win_rate: 0 };
                          return (
                            <div key={tier} className={`rounded-lg p-4 text-center ${
                              tier === 'high' ? 'bg-success/20' :
                              tier === 'medium' ? 'bg-warning/20' : 'bg-red-500/20'
                            }`}>
                              <div className="text-lg font-semibold text-text-primary capitalize">{tier}</div>
                              <div className="text-2xl font-bold font-mono mt-1">
                                {data.win_rate}%
                              </div>
                              <div className="text-xs text-text-secondary">
                                {data.wins}/{data.resolved} wins
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </Card>
              )}

              {/* AI Model Performance */}
              {predictionData.by_model && Object.keys(predictionData.by_model).length > 0 && (
                <Card className="p-6">
                  <h2 className="text-xl font-bold text-text-primary mb-4">AI Model Performance</h2>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    {Object.entries(predictionData.by_model).map(([model, data]) => (
                      <div key={model} className="bg-bg-tertiary rounded-lg p-4">
                        <div className="flex items-center justify-between mb-2">
                          <span className="font-semibold text-text-primary capitalize">{model}</span>
                          <Badge variant="info" size="sm">AI</Badge>
                        </div>
                        <div className="text-3xl font-bold text-accent-blue font-mono">
                          {data.accuracy}%
                        </div>
                        <div className="text-sm text-text-secondary mt-1">
                          {data.correct}/{data.total} correct
                        </div>
                      </div>
                    ))}
                  </div>
                </Card>
              )}
            </>
          )}
        </>
      )}

      {/* Users Tab */}
      {activeTab === 'users' && (
        <>
          {error && (
            <Card className="p-4 bg-red-500/10 border border-red-500/30">
              <div className="text-red-400">{error}</div>
            </Card>
          )}

          {loading ? (
            <div className="flex justify-center py-12">
              <LoadingSpinner size="lg" text="Loading user data..." />
            </div>
          ) : userData && (
            <>
              {/* User Summary */}
              <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                <Card className="p-6">
                  <div className="text-sm text-text-secondary mb-1">Total Users</div>
                  <div className="text-3xl font-bold text-text-primary font-mono">
                    {userData.totals?.total_users || 0}
                  </div>
                </Card>
                <Card className="p-6">
                  <div className="text-sm text-text-secondary mb-1">Active (7d)</div>
                  <div className="text-3xl font-bold text-success font-mono">
                    {userData.totals?.active_users_7d || 0}
                  </div>
                </Card>
                <Card className="p-6">
                  <div className="text-sm text-text-secondary mb-1">Active (30d)</div>
                  <div className="text-3xl font-bold text-accent-blue font-mono">
                    {userData.totals?.active_users_30d || 0}
                  </div>
                </Card>
                <Card className="p-6">
                  <div className="text-sm text-text-secondary mb-1">New (7d)</div>
                  <div className="text-3xl font-bold text-warning font-mono">
                    {userData.totals?.new_users_7d || 0}
                  </div>
                </Card>
                <Card className="p-6">
                  <div className="text-sm text-text-secondary mb-1">New (30d)</div>
                  <div className="text-3xl font-bold text-purple-400 font-mono">
                    {userData.totals?.new_users_30d || 0}
                  </div>
                </Card>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* By Subscription */}
                <Card className="p-6">
                  <h2 className="text-xl font-bold text-text-primary mb-4">By Subscription Tier</h2>
                  <div className="space-y-3">
                    {Object.entries(userData.by_subscription || {}).map(([tier, count]) => (
                      <div key={tier} className="flex justify-between items-center p-3 bg-bg-tertiary rounded-lg">
                        <span className="font-medium text-text-primary capitalize">{tier}</span>
                        <span className="text-2xl font-bold font-mono text-accent-blue">{count}</span>
                      </div>
                    ))}
                  </div>
                </Card>

                {/* Betslip Stats */}
                <Card className="p-6">
                  <h2 className="text-xl font-bold text-text-primary mb-4">Betslip Activity</h2>
                  <div className="space-y-3">
                    <div className="flex justify-between items-center p-3 bg-bg-tertiary rounded-lg">
                      <span className="text-text-secondary">Total Betslips</span>
                      <span className="text-2xl font-bold font-mono text-text-primary">
                        {userData.betslip_stats?.total_betslips || 0}
                      </span>
                    </div>
                    <div className="flex justify-between items-center p-3 bg-bg-tertiary rounded-lg">
                      <span className="text-text-secondary">Active (Pending)</span>
                      <span className="text-2xl font-bold font-mono text-warning">
                        {userData.betslip_stats?.active_betslips || 0}
                      </span>
                    </div>
                    <div className="flex justify-between items-center p-3 bg-bg-tertiary rounded-lg">
                      <span className="text-text-secondary">Users with Betslips</span>
                      <span className="text-2xl font-bold font-mono text-success">
                        {userData.betslip_stats?.users_with_betslips || 0}
                      </span>
                    </div>
                  </div>
                </Card>
              </div>

              {/* Every user: last seen, plan, onboarding emails sent/opened */}
              <UsersTable />

              {/* Refresh Button */}
              <div className="flex justify-end">
                <button
                  onClick={fetchUserData}
                  className="px-4 py-2 bg-accent-blue/20 text-accent-blue rounded-lg hover:bg-accent-blue/30 transition-colors"
                >
                  Refresh User Data
                </button>
              </div>
            </>
          )}
        </>
      )}
    </div>
  );
}
